import {
  Arg,
  INPUT_FIELD_ARGS,
  INPUT_FORM_ARGS,
  Metadata,
  pick,
} from '../../../../utils/spds/metadata';
import { modifier } from 'ember-modifier';
import { on } from '@ember/modifier';
import { or } from 'ember-truth-helpers';
import { testAwareWaitForLater } from '../../../../utils/waiters';
import { tracked } from '@glimmer/tracking';
import Accordion from '../../container/accordion.gjs';
import FlexRow from '../../layout/flex/row.gjs';
import Super from '../../core/base.gjs';
import boldMatchingText from '../../../../modifiers/bold-matching-text';
import styles from './expandable-item.module.scss';

// Matches the accordion content open transition ($timing) so the scroll runs
// after the section has fully expanded.
const EXPAND_ANIMATION_MS = 400;

const METADATA = Metadata.define({
  description: `
Expandable multiselect item: a group checkbox whose label houses a collapsible accordion of additional content (arbitrary markup, e.g. descriptive text or nested options). Clicking the checkbox selects the item. Clicking the accordion trigger toggles the collapsible content without affecting the checkbox.

When a \`@searchValue\` is active, matches are highlighted across the whole item (name and collapsible
content), and the accordion auto-expands when content within the expandable section matches. Clearing the filter releases the accordion back to manual control.`,
  extends: [pick(INPUT_FIELD_ARGS, ['checked', 'inputClasses']), pick(INPUT_FORM_ARGS, ['value'])],
  meta: {
    autoFilter: Arg.boolean({
      default: true,
      description:
        'When true, the filterable menu owns match highlighting; the item only handles auto-expansion. Set false (custom filtering) for the item to highlight matches itself.',
    }),
    checkbox: Arg.component({
      description: 'Group checkbox component rendered for the primary item',
    }),
    label: Arg.string({ description: 'Primary item name shown next to the checkbox' }),
    searchValue: Arg.string({
      description: 'Active filter value; drives match highlighting and auto-expansion',
    }),
  },
  name: 'Multiselect Expandable Item',
});

export default class SpdsMenuMultiselectExpandableItem extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-menu-expandable-item';
  elementsToRegister = [
    { name: 'accordionContent', selector: '.spds-menu-expandable-item-accordion-content' },
    { name: 'accordionTrigger', selector: '.spds-menu-expandable-item-accordion-trigger' },
  ];
  styles = styles;

  @tracked open = false;

  handleAccordionOpen = () => (this.open = true);
  handleAccordionClose = () => (this.open = false);

  evaluateExpansion = modifier((element, [searchValue]) => {
    // Release to manual control on an empty term; do not force the section
    // closed so a cleared filter keeps whatever state the user last set.
    if (!searchValue) return;

    let term = searchValue.trim().toUpperCase();

    this.open = !!this.accordionContent?.textContent?.toUpperCase()?.includes(term);
  });

  // Single scroll path: whenever the section becomes open (manual toggle or
  // search match), wait for the expand animation to settle and scroll the
  // content into view so a bottom-of-list item is not hidden below the fold.
  scrollIntoViewWhenOpen = modifier((element, [open]) => {
    if (!open) return;

    this.#scrollAfterAnimation(element);
  });

  async #scrollAfterAnimation(element) {
    await testAwareWaitForLater(EXPAND_ANIMATION_MS);

    if (this.isDestroyed || this.isDestroying) return;
    if (this.open) element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  preventCheckboxToggle = event => event.preventDefault();

  <template>
    <@checkbox
      @alignItems="start"
      @checked={{@checked}}
      @inputClasses={{@inputClasses}}
      @value={{@value}}
      class={{this.classes}}
      data-test-spds-menu-expandable-item
      {{boldMatchingText (if @autoFilter undefined @searchValue)}}
      {{this.setupComponent}}
      {{this.evaluateExpansion @searchValue}}
      ...attributes
    >
      <:label>
        <Accordion
          @flex="1"
          @onClose={{this.handleAccordionClose}}
          @onOpen={{this.handleAccordionOpen}}
          @open={{this.open}}
          class="spds-menu-expandable-item-accordion"
          {{on "click" this.preventCheckboxToggle}}
          as |A|
        >
          <A.Trigger
            @justifyContent="space-between"
            @reverse={{true}}
            class="spds-menu-expandable-item-accordion-trigger"
            data-test-spds-menu-expandable-item-trigger
          >
            <FlexRow
              @flex="1"
              @tag="span"
              class="spds-menu-expandable-item-accordion-trigger-label"
            >
              {{#if (has-block "label")}}
                {{yield to="label"}}
              {{else}}
                {{@label}}
              {{/if}}
            </FlexRow>
          </A.Trigger>

          <A.ContentContainer
            class="spds-menu-expandable-item-accordion-content"
            data-test-spds-menu-expandable-item-accordion-content
            {{this.scrollIntoViewWhenOpen this.open}}
          >
            {{#if (or (has-block "label") (has-block "content"))}}
              {{yield to="content"}}
            {{else}}
              {{yield}}
            {{/if}}
          </A.ContentContainer>
        </Accordion>
      </:label>
    </@checkbox>
  </template>
}

export const SPDS_MENU_MULTISELECT_EXPANDABLE_ITEM_METADATA =
  SpdsMenuMultiselectExpandableItem.metadata;
